import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, ArrowLeft, ArrowRight, GripVertical, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "@/components/app/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DEPARTMENT_LABELS, useAuth, type Department } from "@/lib/auth-context";
import {
  canCreateDeptBoard,
  canCreatePersonalBoard,
  type BoardType,
  type FieldType,
} from "@/lib/kanban";
import { createKanbanBoard } from "@/lib/kanban.functions";
import { ChipInput } from "@/components/kanban/ChipInput";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/boards/new")({ component: BoardWizardPage });

interface DraftField {
  label: string;
  field_type: FieldType;
  dropdown_options: string[];
}

function BoardWizardPage() {
  const { profile, role } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const viewer = {
    role,
    userId: profile?.id,
    isTeamLead: !!profile?.is_team_lead,
    department: profile?.department ?? null,
  };
  const canDept = canCreateDeptBoard(viewer);
  const canPersonal = canCreatePersonalBoard(viewer);
  const allowedTypes: BoardType[] = [
    ...(canDept ? (["department"] as BoardType[]) : []),
    ...(canPersonal ? (["personal"] as BoardType[]) : []),
  ];

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [type, setType] = useState<BoardType>(allowedTypes[0] ?? "personal");
  const [deptForBoard, setDeptForBoard] = useState<Department>(
    (profile?.department as Department) ?? "editor",
  );
  const [cols, setCols] = useState<string[]>(["To Do", "In Progress", "Done"]);
  const [fields, setFields] = useState<DraftField[]>([]);
  const [busy, setBusy] = useState(false);

  if (allowedTypes.length === 0) {
    return (
      <>
        <AppHeader subtitle="Create a board" />
        <div className="px-[22px]">
          <div className="rounded-xl border border-border p-6 text-[13px]">
            You don't have permission to create boards. Ask an admin to grant you the right permission.
          </div>
        </div>
      </>
    );
  }

  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    if (cols.length < 3 || cols.length > 6) return toast.error("3–6 columns required");
    if (cols.some((c) => !c.trim())) return toast.error("Column names cannot be empty");
    setBusy(true);
    try {
      const res = await createKanbanBoard({
        data: {
          name: name.trim(),
          board_type: type,
          department: type === "department" ? (role === "admin" ? deptForBoard : (profile?.department as string)) : null,
          columns: cols.map((c) => c.trim()),
          fields: fields.map((f) => ({
            label: f.label.trim(),
            field_type: f.field_type,
            dropdown_options: f.field_type === "dropdown" ? f.dropdown_options.filter(Boolean) : null,
          })),
        },
      });
      toast.success("Board created");
      qc.invalidateQueries({ queryKey: ["kanban"] });
      navigate({ to: "/boards/$id", params: { id: res.id } });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AppHeader subtitle="Create a new board — settings lock in after creation" />
      <div className="mx-auto max-w-2xl px-[22px] pb-8">
        <Stepper step={step} />

        {step === 1 && (
          <Card>
            <h2 className="mb-3 text-[15px] font-bold">Board details</h2>
            <div className="grid gap-3">
              <Field label="Board name">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Q3 Video Production" />
              </Field>
              <Field label="Board type">
                <Select value={type} onValueChange={(v) => setType(v as BoardType)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {allowedTypes.includes("department") && (
                      <SelectItem value="department">
                        Department board{role === "admin" ? "" : ` (${profile?.department})`}
                      </SelectItem>
                    )}
                    {allowedTypes.includes("personal") && (
                      <SelectItem value="personal">Personal board</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </Field>
              {type === "department" && role === "admin" && (
                <Field label="Department">
                  <Select value={deptForBoard} onValueChange={(v) => setDeptForBoard(v as Department)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(DEPARTMENT_LABELS) as Department[]).map((d) => (
                        <SelectItem key={d} value={d}>{DEPARTMENT_LABELS[d]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
            <Footer
              onNext={() => setStep(2)}
              nextDisabled={!name.trim()}
              showBack={false}
            />
          </Card>
        )}

        {step === 2 && (
          <Card>
            <h2 className="mb-1 text-[15px] font-bold">Columns</h2>
            <Warning>
              Columns are locked in after creation — they cannot be renamed, reordered, added,
              or removed. To change them later you'd have to archive the board and create a new one.
            </Warning>
            <div className="mt-3 flex flex-col gap-2">
              {cols.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <Input
                    value={c}
                    onChange={(e) => setCols(cols.map((x, idx) => (idx === i ? e.target.value : x)))}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={i === 0 || cols.length <= 3}
                    onClick={() => {
                      const next = [...cols];
                      [next[i - 1], next[i]] = [next[i], next[i - 1]];
                      setCols(next);
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={i === cols.length - 1 || cols.length <= 3}
                    onClick={() => {
                      const next = [...cols];
                      [next[i + 1], next[i]] = [next[i], next[i + 1]];
                      setCols(next);
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={cols.length <= 3}
                    onClick={() => setCols(cols.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                disabled={cols.length >= 6}
                onClick={() => setCols([...cols, "New column"])}
              >
                <Plus className="h-3.5 w-3.5" /> Add column ({cols.length}/6)
              </Button>
            </div>
            <Footer onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </Card>
        )}

        {step === 3 && (
          <Card>
            <h2 className="mb-1 text-[15px] font-bold">Custom fields (optional)</h2>
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-[11px] text-violet-900">
              Add any number of fields now — you can also add, rename, or remove
              fields anytime later from the board's Fields panel. Field type stays
              fixed per field; to change a type, delete and recreate.
            </div>
            <div className="mt-3 flex flex-col gap-3">
              {fields.map((f, i) => (
                <div key={i} className="rounded-lg border border-border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      placeholder="Label (e.g. Aspect ratio)"
                      value={f.label}
                      onChange={(e) =>
                        setFields(fields.map((x, idx) => (idx === i ? { ...x, label: e.target.value } : x)))
                      }
                    />
                    <Select
                      value={f.field_type}
                      onValueChange={(v) =>
                        setFields(
                          fields.map((x, idx) =>
                            idx === i ? { ...x, field_type: v as FieldType } : x,
                          ),
                        )
                      }
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="text">Text</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="date">Date</SelectItem>
                        <SelectItem value="dropdown">Dropdown</SelectItem>
                        <SelectItem value="link">Link</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {f.field_type === "dropdown" && (
                    <div className="mt-2">
                      <ChipInput
                        values={f.dropdown_options}
                        onChange={(opts) =>
                          setFields(
                            fields.map((x, idx) =>
                              idx === i ? { ...x, dropdown_options: opts } : x,
                            ),
                          )
                        }
                        placeholder="Type option, press Enter (paste with commas allowed)"
                      />
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-destructive"
                    onClick={() => setFields(fields.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setFields([...fields, { label: "", field_type: "text", dropdown_options: [] }])
                }
              >
                <Plus className="h-3.5 w-3.5" /> Add field ({fields.length})
              </Button>
            </div>
            <Footer
              onBack={() => setStep(2)}
              nextLabel={busy ? "Creating…" : "Create board"}
              onNext={submit}
              nextDisabled={busy}
            />
          </Card>
        )}
      </div>
    </>
  );
}

function Stepper({ step }: { step: number }) {
  const items = ["Details", "Columns", "Fields"];
  return (
    <div className="mb-4 flex items-center gap-2">
      {items.map((label, i) => {
        const num = i + 1;
        const active = num === step;
        const done = num < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={cn(
                "flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold",
                active
                  ? "bg-[#7C3AED] text-white"
                  : done
                    ? "bg-emerald-500 text-white"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {num}
            </div>
            <span className={cn("text-[11px]", active ? "font-semibold" : "text-muted-foreground")}>
              {label}
            </span>
            {i < items.length - 1 && <div className="h-px w-6 bg-border" />}
          </div>
        );
      })}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card p-5 shadow-sm">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-[11px] font-medium">{label}</Label>
      {children}
    </div>
  );
}

function Warning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-[11px] text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

function Footer({
  onBack,
  onNext,
  showBack = true,
  nextLabel = "Next",
  nextDisabled,
}: {
  onBack?: () => void;
  onNext: () => void;
  showBack?: boolean;
  nextLabel?: string;
  nextDisabled?: boolean;
}) {
  return (
    <div className="mt-5 flex items-center justify-between">
      {showBack ? (
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
      ) : <span />}
      <Button onClick={onNext} disabled={nextDisabled}>
        {nextLabel} <ArrowRight className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
